'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Modal from '@/components/ui/Modal';
import { DailyRecord, FARMS, FLOCK_OPTIONS, FlockOption } from './dailyRecordsData';
import { Loader2, Calculator, AlertCircle } from 'lucide-react';

interface Props {
  open: boolean;
  editRecord: DailyRecord | null;
  onClose: () => void;
  onSave: (record: DailyRecord) => void;
}

interface FormState {
  date: string;
  farm: string;
  house: string;
  flockId: string;
  openingCount: string;
  mortality: string;
  culled: string;
  feedIssued: string;
  feedConsumed: string;
  waterIssued: string;
  waterConsumed: string;
  eggsProduced: string;
  avgTemperature: string;
  humidity: string;
  observations: string;
  recordedBy: string;
  status: 'draft' | 'submitted';
}

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

export default function DailyRecordForm({ open, editRecord, onClose, onSave }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [selectedFlock, setSelectedFlock] = useState<FlockOption | null>(null);

  const [form, setForm] = useState<FormState>({
    date: todayStr(),
    farm: 'Idudi Poultry Farm',
    house: '',
    flockId: '',
    openingCount: '',
    mortality: '0',
    culled: '0',
    feedIssued: '',
    feedConsumed: '',
    waterIssued: '',
    waterConsumed: '',
    eggsProduced: '0',
    avgTemperature: '',
    humidity: '',
    observations: '',
    recordedBy: '',
    status: 'submitted',
  });

  useEffect(() => {
    if (editRecord) {
      setForm({
        date: editRecord.date,
        farm: editRecord.farm,
        house: editRecord.house,
        flockId: editRecord.flockId,
        openingCount: String(editRecord.openingCount),
        mortality: String(editRecord.mortality),
        culled: String(editRecord.culled),
        feedIssued: String(editRecord.feedIssued),
        feedConsumed: String(editRecord.feedConsumed),
        waterIssued: String(editRecord.waterIssued),
        waterConsumed: String(editRecord.waterConsumed),
        eggsProduced: String(editRecord.eggsProduced),
        avgTemperature: String(editRecord.avgTemperature),
        humidity: String(editRecord.humidity),
        observations: editRecord.observations,
        recordedBy: editRecord.recordedBy,
        status: editRecord.status === 'approved' ? 'submitted' : editRecord.status,
      });
      const flock = FLOCK_OPTIONS.find((f) => f.id === editRecord.flockId) ?? null;
      setSelectedFlock(flock);
    }
  }, [editRecord]);

  // Auto-populate opening count when flock is selected
  const handleFlockChange = useCallback((flockId: string) => {
    const flock = FLOCK_OPTIONS.find((f) => f.id === flockId) ?? null;
    setSelectedFlock(flock);
    if (flock) {
      setForm((prev) => ({
        ...prev,
        flockId,
        house: flock.house,
        openingCount: String(flock.currentQty),
      }));
    } else {
      setForm((prev) => ({ ...prev, flockId, openingCount: '' }));
    }
  }, []);

  // Filter flocks by selected farm
  const availableFlocks = FLOCK_OPTIONS.filter((f) => !form.farm || f.farm === form.farm);

  // Auto-calculate closing count
  const opening = parseInt(form.openingCount) || 0;
  const mortality = parseInt(form.mortality) || 0;
  const culled = parseInt(form.culled) || 0;
  const closingCount = Math.max(0, opening - mortality - culled);

  function set(field: keyof FormState, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  function validate(): boolean {
    const errs: Partial<Record<keyof FormState, string>> = {};
    if (!form.date) errs.date = 'Date is required';
    if (!form.farm) errs.farm = 'Farm is required';
    if (!form.flockId) errs.flockId = 'Flock is required';
    if (!form.openingCount || parseInt(form.openingCount) < 0) errs.openingCount = 'Valid opening count required';
    if (!form.feedIssued || parseFloat(form.feedIssued) < 0) errs.feedIssued = 'Feed issued is required';
    if (!form.feedConsumed || parseFloat(form.feedConsumed) < 0) errs.feedConsumed = 'Feed consumed is required';
    if (!form.waterIssued || parseFloat(form.waterIssued) < 0) errs.waterIssued = 'Water issued is required';
    if (!form.waterConsumed || parseFloat(form.waterConsumed) < 0) errs.waterConsumed = 'Water consumed is required';
    if (!form.avgTemperature) errs.avgTemperature = 'Temperature is required';
    if (!form.humidity) errs.humidity = 'Humidity is required';
    if (!form.recordedBy.trim()) errs.recordedBy = 'Recorded by is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit(submitStatus: 'draft' | 'submitted') {
    if (!validate()) return;
    setSubmitting(true);
    // Backend: POST /api/daily-records or PATCH /api/daily-records/:id
    setTimeout(() => {
      const flock = FLOCK_OPTIONS.find((f) => f.id === form.flockId);
      const record: DailyRecord = {
        id: editRecord?.id ?? `dr-${Date.now()}`,
        date: form.date,
        farm: form.farm,
        house: form.house || (flock?.house ?? ''),
        flockId: form.flockId,
        flockNumber: flock?.flockNumber ?? '',
        flockName: flock?.flockName ?? '',
        openingCount: opening,
        mortality,
        culled,
        closingCount,
        feedIssued: parseFloat(form.feedIssued) || 0,
        feedConsumed: parseFloat(form.feedConsumed) || 0,
        waterIssued: parseFloat(form.waterIssued) || 0,
        waterConsumed: parseFloat(form.waterConsumed) || 0,
        eggsProduced: parseInt(form.eggsProduced) || 0,
        avgTemperature: parseFloat(form.avgTemperature) || 0,
        humidity: parseFloat(form.humidity) || 0,
        observations: form.observations,
        recordedBy: form.recordedBy,
        status: submitStatus,
        createdAt: editRecord?.createdAt ?? new Date().toISOString(),
      };
      onSave(record);
      setSubmitting(false);
    }, 700);
  }

  const isLayer = selectedFlock?.birdType === 'Layer' || selectedFlock?.birdType === 'Breeder';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editRecord ? 'Edit Daily Record' : 'New Daily Record'}
      subtitle="Enter today's flock data — closing population is auto-calculated"
      size="xl"
      footer={
        <div className="flex items-center justify-between w-full gap-3">
          <button onClick={onClose} className="btn-secondary text-sm" disabled={submitting}>
            Cancel
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleSubmit('draft')}
              className="btn-secondary text-sm"
              disabled={submitting}
            >
              Save as Draft
            </button>
            <button
              onClick={() => handleSubmit('submitted')}
              className="btn-primary text-sm"
              disabled={submitting}
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
              Submit Record
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-5 py-1">
        {/* Section: Record Info */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 pb-1 border-b">
            Record Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="form-label">Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => set('date', e.target.value)}
                className={`form-input ${errors.date ? 'border-red-400' : ''}`}
              />
              {errors.date && <p className="text-xs text-red-500 mt-1">{errors.date}</p>}
            </div>
            <div>
              <label className="form-label">Farm <span className="text-red-500">*</span></label>
              <select
                value={form.farm}
                onChange={(e) => {
                  set('farm', e.target.value);
                  set('flockId', '');
                  setSelectedFlock(null);
                }}
                className={`form-input ${errors.farm ? 'border-red-400' : ''}`}
              >
                <option value="">Select farm</option>
                {FARMS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              {errors.farm && <p className="text-xs text-red-500 mt-1">{errors.farm}</p>}
            </div>
            <div>
              <label className="form-label">Flock <span className="text-red-500">*</span></label>
              <select
                value={form.flockId}
                onChange={(e) => handleFlockChange(e.target.value)}
                className={`form-input ${errors.flockId ? 'border-red-400' : ''}`}
              >
                <option value="">Select flock</option>
                {availableFlocks.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.flockNumber} — {f.flockName}
                  </option>
                ))}
              </select>
              {errors.flockId && <p className="text-xs text-red-500 mt-1">{errors.flockId}</p>}
            </div>
          </div>
          {selectedFlock && (
            <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg text-xs" style={{ backgroundColor: 'var(--info-bg)', color: 'var(--info)' }}>
              <AlertCircle size={13} />
              <span>
                <strong>{selectedFlock.flockNumber}</strong> · {selectedFlock.birdType} · {selectedFlock.house} · Current population: <strong>{selectedFlock.currentQty.toLocaleString()}</strong> birds
              </span>
            </div>
          )}
        </div>

        {/* Section: Bird Population */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 pb-1 border-b">
            Bird Population
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="form-label">Opening Count <span className="text-red-500">*</span></label>
              <input
                type="number"
                min="0"
                value={form.openingCount}
                onChange={(e) => set('openingCount', e.target.value)}
                placeholder="e.g. 4614"
                className={`form-input font-tabular ${errors.openingCount ? 'border-red-400' : ''}`}
              />
              {errors.openingCount && <p className="text-xs text-red-500 mt-1">{errors.openingCount}</p>}
            </div>
            <div>
              <label className="form-label">Mortality</label>
              <input
                type="number"
                min="0"
                value={form.mortality}
                onChange={(e) => set('mortality', e.target.value)}
                placeholder="0"
                className="form-input font-tabular"
              />
            </div>
            <div>
              <label className="form-label">Culled Birds</label>
              <input
                type="number"
                min="0"
                value={form.culled}
                onChange={(e) => set('culled', e.target.value)}
                placeholder="0"
                className="form-input font-tabular"
              />
            </div>
            <div>
              <label className="form-label flex items-center gap-1">
                <Calculator size={12} style={{ color: 'var(--primary)' }} />
                Closing Count
              </label>
              <div
                className="form-input font-tabular font-semibold flex items-center"
                style={{ backgroundColor: '#f0fdf4', borderColor: 'var(--primary)', color: 'var(--primary)' }}
              >
                {closingCount.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Auto-calculated</p>
            </div>
          </div>
          {(mortality > 0 || culled > 0) && opening > 0 && (
            <div className="mt-2 px-3 py-2 rounded-lg text-xs" style={{ backgroundColor: 'var(--warning-bg)', color: 'var(--warning)' }}>
              Deductions: <strong>{mortality}</strong> mortality + <strong>{culled}</strong> culled = <strong>{mortality + culled}</strong> birds removed · Closing: <strong>{closingCount.toLocaleString()}</strong>
            </div>
          )}
        </div>

        {/* Section: Feed & Water */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 pb-1 border-b">
            Feed & Water (kg / litres)
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="form-label">Feed Issued (kg) <span className="text-red-500">*</span></label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={form.feedIssued}
                onChange={(e) => set('feedIssued', e.target.value)}
                placeholder="e.g. 920"
                className={`form-input font-tabular ${errors.feedIssued ? 'border-red-400' : ''}`}
              />
              {errors.feedIssued && <p className="text-xs text-red-500 mt-1">{errors.feedIssued}</p>}
            </div>
            <div>
              <label className="form-label">Feed Consumed (kg) <span className="text-red-500">*</span></label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={form.feedConsumed}
                onChange={(e) => set('feedConsumed', e.target.value)}
                placeholder="e.g. 905"
                className={`form-input font-tabular ${errors.feedConsumed ? 'border-red-400' : ''}`}
              />
              {errors.feedConsumed && <p className="text-xs text-red-500 mt-1">{errors.feedConsumed}</p>}
            </div>
            <div>
              <label className="form-label">Water Issued (L) <span className="text-red-500">*</span></label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={form.waterIssued}
                onChange={(e) => set('waterIssued', e.target.value)}
                placeholder="e.g. 1840"
                className={`form-input font-tabular ${errors.waterIssued ? 'border-red-400' : ''}`}
              />
              {errors.waterIssued && <p className="text-xs text-red-500 mt-1">{errors.waterIssued}</p>}
            </div>
            <div>
              <label className="form-label">Water Consumed (L) <span className="text-red-500">*</span></label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={form.waterConsumed}
                onChange={(e) => set('waterConsumed', e.target.value)}
                placeholder="e.g. 1820"
                className={`form-input font-tabular ${errors.waterConsumed ? 'border-red-400' : ''}`}
              />
              {errors.waterConsumed && <p className="text-xs text-red-500 mt-1">{errors.waterConsumed}</p>}
            </div>
          </div>
          {form.feedIssued && form.feedConsumed && closingCount > 0 && (
            <div className="mt-2 px-3 py-2 rounded-lg text-xs" style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}>
              Feed per bird: <strong>{(parseFloat(form.feedConsumed) / closingCount * 1000).toFixed(0)}g</strong> ·
              Water per bird: <strong>{(parseFloat(form.waterConsumed || '0') / closingCount).toFixed(2)}L</strong>
            </div>
          )}
        </div>

        {/* Section: Production */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 pb-1 border-b">
            Production
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="form-label">
                Eggs Produced
                {!isLayer && <span className="ml-1 text-xs text-muted-foreground">(broilers: 0)</span>}
              </label>
              <input
                type="number"
                min="0"
                value={form.eggsProduced}
                onChange={(e) => set('eggsProduced', e.target.value)}
                placeholder="0"
                className="form-input font-tabular"
                disabled={!isLayer}
              />
              {isLayer && form.eggsProduced && closingCount > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  HDP: {((parseInt(form.eggsProduced) / closingCount) * 100).toFixed(1)}%
                </p>
              )}
            </div>
            <div>
              <label className="form-label">Avg Temperature (°C) <span className="text-red-500">*</span></label>
              <input
                type="number"
                min="0"
                max="50"
                step="0.1"
                value={form.avgTemperature}
                onChange={(e) => set('avgTemperature', e.target.value)}
                placeholder="e.g. 28.4"
                className={`form-input font-tabular ${errors.avgTemperature ? 'border-red-400' : ''}`}
              />
              {errors.avgTemperature && <p className="text-xs text-red-500 mt-1">{errors.avgTemperature}</p>}
            </div>
            <div>
              <label className="form-label">Humidity (%) <span className="text-red-500">*</span></label>
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={form.humidity}
                onChange={(e) => set('humidity', e.target.value)}
                placeholder="e.g. 68"
                className={`form-input font-tabular ${errors.humidity ? 'border-red-400' : ''}`}
              />
              {errors.humidity && <p className="text-xs text-red-500 mt-1">{errors.humidity}</p>}
            </div>
          </div>
        </div>

        {/* Section: Notes */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 pb-1 border-b">
            Observations & Sign-off
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="form-label">Observations / Notes</label>
              <textarea
                value={form.observations}
                onChange={(e) => set('observations', e.target.value)}
                rows={3}
                placeholder="Any notable observations, health concerns, behaviour changes, equipment issues..."
                className="form-input resize-none"
              />
            </div>
            <div>
              <label className="form-label">Recorded By <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={form.recordedBy}
                onChange={(e) => set('recordedBy', e.target.value)}
                placeholder="Full name of farm manager / officer"
                className={`form-input ${errors.recordedBy ? 'border-red-400' : ''}`}
              />
              {errors.recordedBy && <p className="text-xs text-red-500 mt-1">{errors.recordedBy}</p>}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
